import { useEffect, useState, useCallback, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { AppState } from "@Store/index";

import InfoIcon from "@Icons/InfoIcon";
import Divider from "@Components/Divider";
import useTranslationHook from "hooks/useTranslationHook";
import fetchPaymentInfo from "@Pages/Payment/components/PaymentSummary/helpers/fetchPaymentInfo";
import getCurrencySymbol from "helpers/getCurrencySymbol";
import { updatePaymentSummaryForBookForm } from "@Pages/BookForm/data/bookFormReducer";
import { isEmptyObject } from "helpers/validations";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

import styles from "./PriceSummaryContent.module.scss";
import Tooltip from "@Components/Tooltip/Tooltip";
import wrapperHOC from "@Utils/wrapperHOC";

// ============================================================================
// Type Definitions
// ============================================================================

interface PaymentInfoItem {
  type: string;
  amount: number;
  currency: string;
  label?: string;
  weeks?: number;
  labelSubtext?: string;
  labelText?: string;
  tooltipMsg?: string;
  labelTooltipContent?: string;
}

interface PriceLineItemProps {
  label: string;
  value: string;
  showInfoIcon?: boolean;
  isAdjustment?: boolean;
  labelSubtext?: string;
  labelTooltipContent?: string;
}

interface SectionHeaderProps {
  title: string;
}

interface MockDataResult {
  currency: string;
  booking_releated_fees: PaymentInfoItem[];
  additional_fees: PaymentInfoItem[];
  weeklyRent: { amount: number };
  totalRent: { amount: number; duration: number };
  effectiveTotal: { amount: number };
  leaseDurationUnit: string;
  perDurationUnit: string;
}

type HighlightedFeeType = "deposit" | "refundable_token";

// ============================================================================
// Constants
// ============================================================================

const HIGHLIGHTED_FEE_TYPES: HighlightedFeeType[] = ["deposit", "refundable_token"];
const EXCLUDED_FEE_TYPES = ["gateway_charges", "total"];
const ADVANCE_RENT_TYPE_FIXED = "fixed";
const DURATION_FLEXIBLE = "flexible";
const LONG_LABEL_THRESHOLD = 14;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Formats an amount with currency symbol, handling negative values
 */
const formatAmount = (amount: number, currencySymbol: string): string => {
  const formattedValue = `${currencySymbol}${Math.abs(amount)}`;
  return amount < 0 ? `-${formattedValue}` : formattedValue;
};

/**
 * Gets a human-readable label for a fee item
 */
const getFeeLabel = (feeItem: PaymentInfoItem, t: (key: string) => string): string => {
  if (feeItem?.label) return t(feeItem.label);
  if (!feeItem?.type) return t("Fee");

  const formattedType = feeItem.type.replace(/_/g, " ");
  return formattedType.charAt(0).toUpperCase() + formattedType.slice(1);
};

/**
 * Gets actionable text for payment types (e.g., refund information)
 */
const getPaymentActionableText = (
  type: string,
  isAdvanceRentDepositForTenancy: boolean,
  t: (key: string) => string,
): string => {
  switch (type) {
    case "deposit":
      return isAdvanceRentDepositForTenancy
        ? t("formlayout.inventorycard.priceSummaryContent.adjustableinrentinstalments")
        : t("formlayout.inventorycard.priceSummaryContent.refundedatrentend");
    case "dynamic_platform_fee":
    case "platform_fee":
      return t("formlayout.inventorycard.priceSummaryContent.platformfeetext");
    case "advance_rent":
      return t("formlayout.inventorycard.priceSummaryContent.adjustableinrentinstalments");
    case "refundable_token":
      return t("formlayout.inventorycard.priceSummaryContent.refundabletoken");
    default:
      return "";
  }
};

/**
 * Gets display label text for payment types
 */
const getPaymentLabelText = (type: string, t: (key: string) => string): string => {
  switch (type) {
    case "deposit":
      return t("formlayout.inventorycard.priceSummaryContent.deposit");
    case "advance_rent":
      return t("formlayout.inventorycard.priceSummaryContent.advancerent");
    case "refundable_token":
      return t("formlayout.inventorycard.priceSummaryContent.refundableholdingfee");
    case "platform_fee":
      return t("formlayout.inventorycard.priceSummaryContent.servicefee");
    default:
      return "";
  }
};

/**
 * Type guard to check if a fee type should be highlighted
 */
const isHighlightedFeeType = (type: string): type is HighlightedFeeType =>
  HIGHLIGHTED_FEE_TYPES.includes(type as HighlightedFeeType);

/**
 * Checks if a fee type should be excluded from display
 */
const isExcludedFeeType = (type: string): boolean => EXCLUDED_FEE_TYPES.includes(type);

/**
 * Checks if payment summary data is empty or invalid
 */
const isPaymentSummaryEmpty = (paymentSummary: any): boolean =>
  isEmptyObject(paymentSummary) ||
  paymentSummary === undefined ||
  (Array.isArray(paymentSummary) && paymentSummary.length < 1);

// ============================================================================
// Sub-Components
// ============================================================================

const PriceLineItem = ({
  label,
  value,
  showInfoIcon = false,
  isAdjustment = false,
  labelSubtext = "",
  labelTooltipContent = "",
}: PriceLineItemProps) => (
  <>
    <div className={styles.priceLineItem}>
      <div className={formatClassNames(styles.label)}>
        {label}
        {showInfoIcon && (
          <Tooltip
            content={labelTooltipContent || "sample tooltip content"}
            direction={label.length < LONG_LABEL_THRESHOLD ? "right" : "bottom"}
            classNameWrapper={styles.tooltip}
            className={styles.tooltipInnerClass}
          >
            <InfoIcon width="12" height="12" fill="#111928" />
          </Tooltip>
        )}
      </div>
      <div className={formatClassNames(styles.value)}>{value}</div>
    </div>
    {isAdjustment && (
      <div className={styles.priceLineItem}>
        <div className={formatClassNames(styles.label, styles.adjustmentLabel)}>
          {label} {labelSubtext}
        </div>
        <div className={formatClassNames(styles.value, styles.adjustment)}>-{value}</div>
      </div>
    )}
  </>
);

const SectionHeader = ({ title }: SectionHeaderProps) => (
  <div className={styles.sectionHeader}>{title}</div>
);

// ============================================================================
// Main Component
// ============================================================================

const PriceSummaryContent = () => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const [isTotalRentExpanded] = useState(true);
  const [storedNationality, setStoredNationality] = useState<string>("");

  const {
    personalDetails,
    inventoryData,
    accommodationDetails,
    nationality,
    paymentSummaryForBookForm,
    userCurrency,
  } = useSelector(
    ({ bookFormData, appData }: AppState) => ({
      accommodationDetails: bookFormData?.formData?.accommodationDetails,
      paymentSummaryForBookForm: bookFormData.paymentSummaryForBookForm,
      personalDetails: bookFormData?.formData?.personalDetails,
      inventoryData: bookFormData?.data,
      defaultState: bookFormData.initialFromState,
      nationality: appData.nationality,
      userCurrency: appData.userCurrency,
    }),
    shallowEqual,
  );

  // Derived values
  const nationalityForPaymentSummary = personalDetails?.["data/nationality"] || nationality || "";

  const tenanciesObjForPaymentSummary = useMemo(() => {
    const roomTypeId = Number(accommodationDetails?.roomType);
    const tenancyId = Number(accommodationDetails?.tenancyId);

    const selectedRoom = inventoryData?.active_children?.find(
      (room: any) => room?.id === roomTypeId,
    );

    if (!selectedRoom) return null;

    // Try to find specific tenancy, fallback to room if not found
    return (
      selectedRoom.active_children?.find((tenancy: any) => tenancy?.id === tenancyId) ||
      selectedRoom
    );
  }, [
    inventoryData?.active_children,
    accommodationDetails?.roomType,
    accommodationDetails?.tenancyId,
  ]);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const getPaymentInfoAndSummaryObject = useCallback(async () => {
    const inventoryId =
      accommodationDetails.tenancyId ||
      accommodationDetails.roomId ||
      accommodationDetails.inventory_id;

    const res = await fetchPaymentInfo({
      id: inventoryData?.canonical_name,
      inventory_id: inventoryId,
      source: "paymentSummaryModal",
      nationality: nationalityForPaymentSummary,
      userCurrency,
    });

    if (!isEmptyObject(res)) {
      dispatch(updatePaymentSummaryForBookForm(res?.payment_info));
    }
  }, [
    inventoryData?.canonical_name,
    accommodationDetails.tenancyId,
    accommodationDetails.roomId,
    accommodationDetails.inventory_id,
    nationalityForPaymentSummary,
    dispatch,
  ]);

  // Effect to handle nationality changes and initial load
  useEffect(() => {
    const hasNationalityChanged =
      nationalityForPaymentSummary &&
      storedNationality &&
      nationalityForPaymentSummary !== storedNationality;

    const isInitialLoadWithNationality = nationalityForPaymentSummary && !storedNationality;

    // Fetch when nationality changes or on initial load with nationality
    if (hasNationalityChanged || isInitialLoadWithNationality) {
      getPaymentInfoAndSummaryObject();
      setStoredNationality(nationalityForPaymentSummary);
      return;
    }

    // Fallback: fetch if no nationality and no payment summary data
    if (!nationalityForPaymentSummary && !storedNationality) {
      const hasNoPaymentSummary = isPaymentSummaryEmpty(paymentSummaryForBookForm);

      if (hasNoPaymentSummary) {
        getPaymentInfoAndSummaryObject();
      }
    }
  }, [
    nationalityForPaymentSummary,
    storedNationality,
    paymentSummaryForBookForm,
    getPaymentInfoAndSummaryObject,
  ]);

  // ============================================================================
  // Data Processing
  // ============================================================================

  /**
   * Calculates the total rent based on pricing and duration
   */
  const calculateTotalRent = useCallback((): number => {
    const weeklyPrice =
      tenanciesObjForPaymentSummary?.pricing?.available_price ||
      tenanciesObjForPaymentSummary?.pricing?.min_available_price ||
      0;

    const isFlexibleDuration =
      accommodationDetails?.duration === DURATION_FLEXIBLE ||
      Boolean(accommodationDetails?.arrival_date);

    const duration = isFlexibleDuration
      ? Number(accommodationDetails?.custom_duration?.split(" ")[0]) ||
        Number(accommodationDetails?.duration?.split(" ")[0]) ||
        0
      : tenanciesObjForPaymentSummary?.meta?.available_lease_duration || 0;

    return weeklyPrice * duration;
  }, [tenanciesObjForPaymentSummary, accommodationDetails]);

  /**
   * Normalizes payment summary to always return an array
   */
  const normalizePaymentSummary = useCallback(
    (): PaymentInfoItem[] =>
      Array.isArray(paymentSummaryForBookForm) ? paymentSummaryForBookForm : [],
    [paymentSummaryForBookForm],
  );

  /**
   * Extracts booking-related fees from payment summary
   */
  const extractBookingRelatedFees = useCallback(
    (
      paymentSummary: PaymentInfoItem[],
      isAdvanceRentDepositForTenancy: boolean,
    ): PaymentInfoItem[] =>
      paymentSummary.reduce<PaymentInfoItem[]>((acc, feeItem) => {
        if (isHighlightedFeeType(feeItem?.type)) {
          acc.push({
            ...feeItem,
            labelSubtext: getPaymentActionableText(
              feeItem?.type,
              isAdvanceRentDepositForTenancy,
              t,
            ),
            labelText: getPaymentLabelText(feeItem?.type, t),
            labelTooltipContent: feeItem?.tooltipMsg,
          });
        }
        return acc;
      }, []),
    [t],
  );

  /**
   * Extracts additional fees from payment summary
   */
  const extractAdditionalFees = useCallback(
    (paymentSummary: PaymentInfoItem[]): PaymentInfoItem[] =>
      paymentSummary.filter(
        (feeItem) =>
          feeItem?.type && !isHighlightedFeeType(feeItem.type) && !isExcludedFeeType(feeItem.type),
      ),
    [],
  );

  /**
   * Generates mock data structure for rendering
   */
  const getPriceSummaryCleanData = useCallback((): MockDataResult => {
    const normalizedPaymentSummary = normalizePaymentSummary();
    const isAdvanceRentDepositForTenancy =
      tenanciesObjForPaymentSummary?.pricing?.advance_rent_type === ADVANCE_RENT_TYPE_FIXED;

    const bookingRelatedFees = extractBookingRelatedFees(
      normalizedPaymentSummary,
      isAdvanceRentDepositForTenancy,
    );

    const additionalFees = extractAdditionalFees(normalizedPaymentSummary);
    const additionalFeesTotal = additionalFees.reduce((sum, fee) => sum + (fee.amount || 0), 0);

    const totalRentAmount = calculateTotalRent();
    const effectiveTotalAmount = totalRentAmount + additionalFeesTotal;

    const isFlexibleDuration =
      accommodationDetails?.duration === DURATION_FLEXIBLE ||
      Boolean(accommodationDetails?.arrival_date);

    const leaseDuration = isFlexibleDuration
      ? Number(accommodationDetails?.custom_duration?.split(" ")[0]) ||
        Number(accommodationDetails?.duration?.split(" ")[0]) ||
        0
      : tenanciesObjForPaymentSummary?.meta?.available_lease_duration || 0;

    const weeklyRentAmount =
      tenanciesObjForPaymentSummary?.pricing?.available_price ||
      tenanciesObjForPaymentSummary?.pricing?.min_available_price ||
      0;

    const currencySymbol = getCurrencySymbol(tenanciesObjForPaymentSummary?.pricing?.currency);
    const durationUnit = tenanciesObjForPaymentSummary?.meta?.lease_duration_unit || "";
    const leaseDurationUnit = t(`${durationUnit}${leaseDuration > 1 ? "s" : ""}`);

    return {
      currency: currencySymbol,
      booking_releated_fees: bookingRelatedFees,
      additional_fees: additionalFees,
      weeklyRent: { amount: weeklyRentAmount },
      totalRent: { amount: totalRentAmount, duration: leaseDuration },
      effectiveTotal: { amount: effectiveTotalAmount },
      leaseDurationUnit,
      perDurationUnit: durationUnit,
    };
  }, [
    normalizePaymentSummary,
    extractBookingRelatedFees,
    extractAdditionalFees,
    calculateTotalRent,
    tenanciesObjForPaymentSummary,
    accommodationDetails,
    t,
  ]);

  const mockData = useMemo(() => getPriceSummaryCleanData(), [getPriceSummaryCleanData]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleTotalRentToggle = () => {
    // Placeholder for future expansion toggle functionality
  };

  const handleTotalRentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleTotalRentToggle();
    }
  };

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const hasBookingFees = mockData.booking_releated_fees?.length > 0;
  const hasAdditionalFees = mockData.additional_fees?.length > 0;
  const hasRentDetails = Boolean(mockData.weeklyRent || mockData.totalRent);

  const renderBookingFees = () => (
    <>
      <SectionHeader title={t("formlayout.inventorycard.priceSummaryContent.bookingrelatedfee")} />
      {mockData.booking_releated_fees?.map((feeItem) => (
        <PriceLineItem
          key={`${feeItem.type}-${feeItem.label || feeItem.amount}`}
          label={feeItem.labelText || ""}
          value={formatAmount(feeItem.amount, mockData.currency)}
          showInfoIcon
          isAdjustment
          labelSubtext={feeItem?.labelSubtext}
          labelTooltipContent={feeItem?.labelTooltipContent}
        />
      ))}
    </>
  );

  const renderAdditionalFees = () => (
    <>
      {hasBookingFees && <Divider color="#E5E7EB" dividerClassName={styles.dividerMargin} />}
      <SectionHeader title={t("Additional Fee")} />
      {mockData.additional_fees?.map((feeItem) => (
        <PriceLineItem
          key={`${feeItem.type}-${feeItem.label || feeItem.amount}`}
          label={getFeeLabel(feeItem, t)}
          value={formatAmount(feeItem.amount, mockData.currency)}
        />
      ))}
    </>
  );

  const renderRentDetails = () => (
    <>
      <div className={styles.totalRentHeader}>
        <SectionHeader title={t("formlayout.inventorycard.priceSummaryContent.totalrent")} />

        <div
          className={styles.totalRentSummary}
          onClick={handleTotalRentToggle}
          onKeyDown={handleTotalRentKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isTotalRentExpanded}
          aria-label={t("Toggle total rent details")}
        />
      </div>
      <div
        className={`${styles.rentDetailsContainer} ${
          isTotalRentExpanded ? styles.expanded : styles.collapsed
        }`}
      >
        {mockData.totalRent && (
          <PriceLineItem
            label={t(
              `${mockData.currency}${mockData.weeklyRent?.amount || 0}/${
                mockData.perDurationUnit
              } x ${mockData.totalRent.duration || 0} ${mockData.leaseDurationUnit}`,
            )}
            value={`${mockData.currency}${mockData.totalRent.amount}`}
          />
        )}
      </div>
    </>
  );

  const renderEffectiveTotal = () => (
    <>
      <Divider color="#E5E7EB" dividerClassName={styles.dividerMargin} />
      <div className={styles.effectiveTotalRow}>
        <div className={styles.effectiveTotalLabel}>
          {t("formlayout.inventorycard.priceSummaryContent.effectivetotalcost")}
        </div>
        <div className={styles.effectiveTotalValue}>
          {mockData.currency}
          {mockData.effectiveTotal.amount}
        </div>
      </div>
    </>
  );

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className={styles.priceSummaryContent}>
      {hasBookingFees && renderBookingFees()}

      {hasAdditionalFees && renderAdditionalFees()}

      {(hasBookingFees || hasAdditionalFees) && hasRentDetails && (
        <Divider color="#E5E7EB" dividerClassName={styles.dividerMargin} />
      )}

      {hasRentDetails && renderRentDetails()}

      {mockData.effectiveTotal && renderEffectiveTotal()}
    </div>
  );
};

export default wrapperHOC(PriceSummaryContent, {
  componentName: "PriceSummaryContent",
  showForChina: true,
});
